import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DISCUSSION_ROOM_WIDGET_INSTRUCTION_DISMISSED_KEY } from '../constants/discussionRooms';

const POSITION_KEY = 'discussion-room-widget-position';
const WIDGET_WIDTH = 280;
const WIDGET_HEIGHT = 112;
const EDGE_PADDING = 8;

interface Position {
  left: number;
  top: number;
}

const clampPosition = (position: Position): Position => ({
  left: Math.min(Math.max(EDGE_PADDING, position.left), Math.max(EDGE_PADDING, window.innerWidth - WIDGET_WIDTH - EDGE_PADDING)),
  top: Math.min(Math.max(EDGE_PADDING, position.top), Math.max(EDGE_PADDING, window.innerHeight - WIDGET_HEIGHT - EDGE_PADDING)),
});

const DiscussionRoomsWidget: React.FC = () => {
  const { isAuthenticated, user, token } = useAuth();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; left: number; top: number; pointerId: number } | null>(null);
  const movedRef = useRef(false);
  const [showInstruction, setShowInstruction] = useState(() => {
    try {
      return localStorage.getItem(DISCUSSION_ROOM_WIDGET_INSTRUCTION_DISMISSED_KEY) !== 'true';
    } catch {
      return true;
    }
  });
  const [position, setPosition] = useState<Position | null>(() => {
    try {
      const saved = localStorage.getItem(POSITION_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (Number.isFinite(parsed?.left) && Number.isFinite(parsed?.top)) return parsed;
    } catch {
      // Ignore invalid or unavailable local storage.
    }
    return null;
  });

  const isHighSchoolUser = isAuthenticated && user?.educationLevel === 'high-school';

  useEffect(() => {
    try {
      setShowInstruction(localStorage.getItem(DISCUSSION_ROOM_WIDGET_INSTRUCTION_DISMISSED_KEY) !== 'true');
    } catch {
      setShowInstruction(true);
    }
  }, [token]);

  useEffect(() => {
    if (!position) return undefined;
    const clampToViewport = () => setPosition((current) => current ? clampPosition(current) : current);
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, [position]);

  if (!isHighSchoolUser || typeof document === 'undefined') return null;

  const startDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const bounds = rootRef.current?.getBoundingClientRect();
    if (!bounds) return;
    movedRef.current = false;
    dragStartRef.current = { x: event.clientX, y: event.clientY, left: bounds.left, top: bounds.top, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const moveDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const dragStart = dragStartRef.current;
    if (!dragStart || dragStart.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - dragStart.x) + Math.abs(event.clientY - dragStart.y) > 4) movedRef.current = true;
    setPosition(clampPosition({
      left: dragStart.left + event.clientX - dragStart.x,
      top: dragStart.top + event.clientY - dragStart.y,
    }));
  };

  const endDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragStartRef.current?.pointerId !== event.pointerId) return;
    dragStartRef.current = null;
    setPosition((current) => {
      if (current) {
        try { localStorage.setItem(POSITION_KEY, JSON.stringify(current)); } catch {}
      }
      return current;
    });
  };

  return createPortal(
    <Box
      ref={rootRef}
      sx={{
        position: 'fixed',
        left: position?.left,
        top: position?.top,
        bottom: position ? 'auto' : 24,
        zIndex: 2300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.5,
        p: 0.75,
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        boxShadow: 4,
        touchAction: 'none',
      }}
    >
      {showInstruction && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', pl: 1 }}>
          <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.35 }}>
            Join subject discussion rooms for live peer learning.
          </Typography>
          <Tooltip title="Dismiss this tip">
            <IconButton
              aria-label="Dismiss discussion room tip"
              size="small"
              onClick={() => {
                setShowInstruction(false);
                try {
                  localStorage.setItem(DISCUSSION_ROOM_WIDGET_INSTRUCTION_DISMISSED_KEY, 'true');
                } catch {
                  setShowInstruction(false);
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Tooltip title="Open subject discussion rooms" arrow>
          <IconButton
            onClick={() => navigate('/discussion-rooms')}
            aria-label="Open subject discussion rooms"
            sx={{ width: 44, height: 44, bgcolor: 'success.main', color: 'success.contrastText', '&:hover': { bgcolor: 'success.dark' } }}
          >
            <ForumOutlinedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Drag to move discussion room widget" arrow disableTouchListener>
          <IconButton
            aria-label="Move discussion room widget"
            onPointerDown={startDrag}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClick={(event) => {
              if (movedRef.current) {
                event.preventDefault();
                event.stopPropagation();
                movedRef.current = false;
              }
            }}
            size="small"
            sx={{ cursor: 'grab', touchAction: 'none', color: 'text.secondary' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>,
    document.body
  );
};

export default DiscussionRoomsWidget;